/** ADR-037: old task tabs become Body/Look; preserve sizes and collapsed zones.
 * Only v1 is migrated. Validate retired vocabulary and shape before merging so
 * an invalid preference cannot be hidden by deduplication. No upgrade notice.
 */
const oldIds = new Set(['timeline', 'hands', 'character', 'materials', 'zones', 'package']);
const newId = (id: string): string => ['hands', 'character', 'zones'].includes(id) ? 'body' : id === 'materials' ? 'look' : id;

export function migrateWorkspace(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw;
  const layout = raw as Record<string, unknown>;
  if (layout['schemaVersion'] !== '1') return raw;
  const zones = layout['zones'];
  if (typeof zones !== 'object' || zones === null) throw new Error('workspace migration: zones missing');
  const seenOld = new Set<string>();
  const seenNew = new Set<string>();
  const migrated: Record<string, unknown[]> = {};
  for (const zone of ['left', 'right', 'bottom']) {
    const groups = (zones as Record<string, unknown>)[zone];
    if (!Array.isArray(groups)) throw new Error(`workspace migration: ${zone} is not an array`);
    migrated[zone] = groups.flatMap((group: unknown) => {
      if (typeof group !== 'object' || group === null) throw new Error('workspace migration: invalid group');
      const g = group as Record<string, unknown>;
      if (!Array.isArray(g['panels']) || g['panels'].length === 0) throw new Error('workspace migration: empty panel group');
      if (typeof g['size'] !== 'number' || !Number.isFinite(g['size']) || g['size'] < 0.1 || g['size'] > 0.9) throw new Error('workspace migration: invalid group size');
      const before = g['panels'].map((p: unknown) => {
        if (typeof p !== 'object' || p === null) throw new Error('workspace migration: invalid placement');
        const placement = p as Record<string, unknown>;
        const id = placement['id'];
        if (typeof id !== 'string' || !oldIds.has(id)) throw new Error(`workspace migration: unknown panel ${String(id)}`);
        if (seenOld.has(id)) throw new Error(`workspace migration: duplicate panel ${id}`);
        seenOld.add(id);
        const register = placement['register'];
        if (register !== undefined && register !== 'expert' && register !== 'creator') throw new Error('workspace migration: invalid register');
        return { ...placement, id };
      });
      if (!before.some(p => p.id === g['active'])) throw new Error('workspace migration: active tab absent');
      const panels = before.flatMap(p => {
        const id = newId(p.id);
        if (id === 'package' || seenNew.has(id)) return [];
        seenNew.add(id);
        return [{ ...p, id }];
      });
      if (!panels.length) return [];
      const active = newId(g['active'] as string);
      return [{ ...g, panels, active: panels.some(p => p.id === active) ? active : panels[0]!.id }];
    });
  }
  return { ...layout, schemaVersion: '2', zones: migrated };
}
